import React, { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Button, Input, Textarea, Label, Select } from "@/components/Primitives";
import { toast } from "sonner";
import { Camera, User, KeyRound, Bell, Check } from "lucide-react";

function formatErr(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return e?.message || "Something went wrong";
}

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const [tab, setTab] = useState("edit");

  return (
    <div className="space-y-6">
      <PageHeader breadcrumb="Account · Profile" title="Your profile" subtitle="How you appear in the dashboard." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left summary */}
        <Card className="lg:col-span-1">
          <div className="p-6 flex flex-col items-center text-center">
            <div className="relative">
              <img
                src={user?.avatar_url || "/brand/soubor-one-mark.png"}
                alt=""
                className="w-28 h-28 rounded-full object-cover ring-4 ring-[hsl(var(--primary-muted))]"
              />
              <div className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-[hsl(var(--primary))] text-white flex items-center justify-center ring-4 ring-[hsl(var(--surface))]">
                <Camera className="w-4 h-4" strokeWidth={1.8} />
              </div>
            </div>
            <div className="mt-4 font-bold text-[18px]">{user?.name}</div>
            <div className="text-[13px] text-[hsl(var(--text-muted))]">{user?.email}</div>
            <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))] text-[11px] font-semibold uppercase tracking-widest">
              {user?.role}
            </div>
          </div>
          <div className="px-6 pb-6">
            <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold mb-3">Personal info</div>
            <div className="space-y-2.5 text-[13px]">
              <Row k="Full name" v={user?.name || "—"} />
              <Row k="Email" v={user?.email || "—"} />
              <Row k="Phone" v={user?.phone || "—"} />
              <Row k="Department" v={user?.department || "—"} />
              <Row k="Designation" v={user?.designation || "—"} />
              <Row k="Language" v={user?.language || "English"} />
              <Row k="Bio" v={user?.bio || "—"} multi />
            </div>
          </div>
        </Card>

        {/* Right: tabs */}
        <Card className="lg:col-span-2 p-0">
          <div className="flex border-b border-[hsl(var(--border))]">
            {[
              { id: "edit", l: "Edit Profile", i: User },
              { id: "password", l: "Change Password", i: KeyRound },
              { id: "notifs", l: "Notification Settings", i: Bell, soon: true },
            ].map((t) => (
              <button
                key={t.id}
                disabled={t.soon}
                onClick={() => !t.soon && setTab(t.id)}
                className={`relative inline-flex items-center gap-2 px-5 py-3.5 text-[13px] font-semibold transition-all ${
                  tab === t.id ? "text-[hsl(var(--primary))]" : t.soon ? "text-[hsl(var(--text-subtle))] cursor-not-allowed" : "text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]"
                }`}
                data-testid={`profile-tab-${t.id}`}
              >
                <t.i className="w-4 h-4" strokeWidth={1.6}/>{t.l}
                {t.soon && <span className="ml-1 text-[9px] uppercase tracking-widest bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))] px-1.5 py-0.5 rounded">coming soon</span>}
                {tab === t.id && !t.soon && <span className="absolute -bottom-px left-4 right-4 h-0.5 bg-[hsl(var(--primary))]" />}
              </button>
            ))}
          </div>
          <div className="p-6">
            {tab === "edit" && <EditProfile user={user} onSaved={refresh} />}
            {tab === "password" && <ChangePassword />}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Row({ k, v, multi }) {
  return (
    <div className={`flex ${multi ? "flex-col gap-1" : "items-center justify-between"}`}>
      <div className="text-[hsl(var(--text-muted))]">{k}</div>
      <div className="font-medium break-words">{v}</div>
    </div>
  );
}

function EditProfile({ user, onSaved }) {
  const [form, setForm] = useState({
    name: user?.name || "", phone: user?.phone || "",
    department: user?.department || "", designation: user?.designation || "",
    language: user?.language || "English", bio: user?.bio || "",
    avatar_url: user?.avatar_url || "",
  });
  const [saving, setSaving] = useState(false);
  const s = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/auth/me", form);
      await onSaved?.();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setSaving(false); }
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        // Resize to max 256×256 and re-encode as JPEG @ 0.85 to keep base64 small (~15-25KB)
        const size = 256;
        const scale = Math.min(size / img.width, size / img.height, 1);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        const dataUri = canvas.toDataURL("image/jpeg", 0.85);
        setForm((f) => ({ ...f, avatar_url: dataUri }));
        toast.success("Image ready. Click Save to apply");
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  return (
    <form onSubmit={save} className="space-y-5">
      <div>
        <Label>Profile picture</Label>
        <div className="flex items-center gap-4 mt-1">
          <img src={form.avatar_url || "/brand/soubor-one-mark.png"} className="w-16 h-16 rounded-full object-cover ring-2 ring-[hsl(var(--primary-muted))]" alt=""/>
          <div className="flex-1 space-y-2">
            <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] cursor-pointer text-[13px] font-semibold transition-colors" data-testid="profile-avatar-upload">
              Upload image…
              <input type="file" accept="image/*" onChange={onPickFile} className="hidden"/>
            </label>
            {form.avatar_url && <button type="button" onClick={() => setForm({ ...form, avatar_url: "" })} className="ml-2 text-[12px] text-rose-600 hover:underline" data-testid="profile-avatar-clear">Remove</button>}
            <div>
              <Input value={form.avatar_url?.startsWith("data:") ? "" : form.avatar_url} onChange={s("avatar_url")} placeholder="…or paste an image URL" data-testid="profile-avatar-input"/>
            </div>
          </div>
        </div>
        <div className="text-[11px] text-[hsl(var(--text-muted))] mt-1">Images are auto-resized to 256×256 for fast loading.</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><Label>Full name *</Label><Input required value={form.name} onChange={s("name")} data-testid="profile-name"/></div>
        <div>
          <Label>Email *</Label>
          <Input value={user?.email || ""} disabled className="opacity-60 cursor-not-allowed"/>
          <div className="text-[11px] text-[hsl(var(--text-muted))] mt-1">Changing email may require your current password.</div>
        </div>
        <div><Label>Phone</Label><Input value={form.phone} onChange={s("phone")} placeholder="+44 7…"/></div>
        <div><Label>Department</Label><Input value={form.department} onChange={s("department")} placeholder="Head Office / Ops"/></div>
        <div><Label>Designation</Label><Input value={form.designation} onChange={s("designation")} placeholder="Founder / CTO / Ops Lead"/></div>
        <div>
          <Label>Language</Label>
          <Select value={form.language} onChange={s("language")}>
            <option>English</option><option>French</option><option>German</option><option>Spanish</option><option>Italian</option>
          </Select>
        </div>
      </div>
      <div>
        <Label>Bio</Label>
        <Textarea value={form.bio} onChange={s("bio")} rows={4} placeholder="Tell the team a little about you…"/>
      </div>
      <div className="flex gap-2 pt-1">
        <Button type="submit" disabled={saving} data-testid="save-profile-btn">{saving ? "Saving…" : <><Check className="w-4 h-4" strokeWidth={2}/>Save changes</>}</Button>
        <Button type="button" variant="secondary" onClick={() => setForm({ ...form, name: user?.name || "" })}>Cancel</Button>
      </div>
    </form>
  );
}

function ChangePassword() {
  const [current, setCurrent] = useState("");
  const [next1, setNext1] = useState("");
  const [next2, setNext2] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next1 !== next2) { toast.error("Passwords don't match"); return; }
    if (next1.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next1 });
      toast.success("Password updated");
      setCurrent(""); setNext1(""); setNext2("");
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="max-w-md space-y-4">
      <div><Label>Current password *</Label><Input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="current-password"/></div>
      <div><Label>New password *</Label><Input type="password" required value={next1} onChange={(e) => setNext1(e.target.value)} data-testid="new-password"/></div>
      <div><Label>Confirm new password *</Label><Input type="password" required value={next2} onChange={(e) => setNext2(e.target.value)} data-testid="confirm-password"/></div>
      <Button type="submit" disabled={saving} data-testid="change-password-btn">{saving ? "Updating…" : "Update password"}</Button>
    </form>
  );
}
